import React from 'react';
import { Box, Typography, Modal, Button, Chip, Divider, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '92%', sm: 480 },
    bgcolor: '#ffffff',
    boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)',
    borderRadius: 3,
    p: 3.5,
    outline: 'none'
};

export default function AlertDetailsModal({ open, onClose, alert, onResolve, onDismiss }) {
    const navigate = useNavigate();
    if (!alert) return null;

    const severity = (alert.title || alert.type || 'Info').toLowerCase();
    const isCritical = severity.includes('critical') || severity.includes('error');
    const isWarning = severity.includes('warning') || severity.includes('alert');

    const typeColor = isCritical ? '#ef4444' : (isWarning ? '#f59e0b' : '#3b82f6');
    const typeBg = isCritical ? '#fee2e2' : (isWarning ? '#fef3c7' : '#e0f2fe');
    const IconComponent = isCritical ? ErrorOutlineIcon : (isWarning ? WarningAmberIcon : InfoOutlinedIcon);

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                {/* Header with Title and Close button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ 
                            width: 38, 
                            height: 38, 
                            borderRadius: '10px', 
                            bgcolor: typeBg, 
                            color: typeColor, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            <IconComponent sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                                Alert Details
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                ID: {alert.id || 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} />

                {/* Severity & Time */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip 
                        label={alert.title || alert.type || 'Alert'} 
                        size="small"
                        sx={{ 
                            bgcolor: typeBg, 
                            color: typeColor, 
                            fontWeight: 700, 
                            fontSize: '0.75rem',
                            borderRadius: '6px'
                        }} 
                    />
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                        {alert.timeAgo || 'Recently recorded'}
                    </Typography>
                </Box>

                {/* Message Body */}
                <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    mb: 3 
                }}>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontSize: '0.88rem', fontWeight: 500, lineHeight: 1.5 }}>
                        {alert.description || 'No description available for this alert.'}
                    </Typography>
                </Box>

                {/* Context-aware Navigation Button (if applicable) */}
                {alert.targetRoute && (
                    <Box sx={{ mb: 2.5 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            endIcon={<OpenInNewIcon sx={{ fontSize: '1rem !important' }} />}
                            onClick={() => {
                                onClose();
                                navigate(alert.targetRoute.path);
                            }}
                            sx={{
                                py: 1,
                                borderRadius: 2,
                                borderColor: '#cbd5e1',
                                color: '#334155',
                                fontWeight: 600,
                                fontSize: '0.82rem',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#94a3b8',
                                    bgcolor: '#f8fafc'
                                }
                            }}
                        >
                            Navigate to {alert.targetRoute.label}
                        </Button>
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
                    <Button 
                        onClick={() => {
                            onClose();
                            if (onDismiss) onDismiss(alert.id);
                        }}
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: 600, 
                            fontSize: '0.8rem',
                            borderRadius: 2,
                            px: 2
                        }}
                    >
                        Dismiss
                    </Button>
                    <Button 
                        onClick={() => {
                            onClose();
                            if (onResolve) onResolve(alert.id);
                        }}
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircleOutlineIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: 600, 
                            fontSize: '0.8rem',
                            borderRadius: 2,
                            px: 2.5,
                            boxShadow: 'none'
                        }}
                    >
                        Resolve Alert
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
